/* Feb-2026 · Running Cost Pill for the top-nav.
 *
 * Shows the current project's month-to-date AI spend + effective cap.
 * Polls every 30 seconds while mounted. Turns amber at 75% of cap,
 * red at 95%. Click opens the Settings dialog on the AI budget tab.
 *
 * Hidden entirely when no project is active or when the user has no
 * budget cap set — the pill exists to show writers their meter, not
 * to nag people who never wanted a cap in the first place.
 */
import React, { useEffect, useState } from 'react';
import { DollarSign, Loader2 } from 'lucide-react';
import { usePW } from '@/lib/store';
import { budgetApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { fmtUsd } from '@/lib/cost';

const POLL_MS = 30_000;

export default function RunningCostPill({ onClick }) {
  const { project } = usePW();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!project?.id) { setData(null); return undefined; }
    let cancelled = false;
    const load = async () => {
      setBusy(true);
      try {
        const r = await budgetApi.project(project.id);
        if (!cancelled) setData(r);
      } catch {
        /* silent — pill just disappears */
      } finally {
        if (!cancelled) setBusy(false);
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [project?.id]);

  if (!project?.id || !data) return null;

  const spent = Number(data.spent_usd) || 0;
  const cap   = data.cap_usd != null ? Number(data.cap_usd) : null;
  const pct   = cap ? Math.min(100, (spent / cap) * 100) : 0;

  const tone =
    !cap             ? 'muted'
    : pct >= 95      ? 'danger'
    : pct >= 75      ? 'warn'
                     :  'ok';

  const toneClass = {
    muted:  'bg-secondary text-muted-foreground border-border',
    ok:     'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800/40',
    warn:   'bg-amber-100  text-amber-900  border-amber-300  dark:bg-amber-950/40  dark:text-amber-200  dark:border-amber-800/40',
    danger: 'bg-red-100    text-red-900    border-red-300    dark:bg-red-950/40    dark:text-red-200    dark:border-red-800/40',
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      title={cap
        ? `AI spend this month · ${fmtUsd(spent)} of ${fmtUsd(cap)} (${pct.toFixed(0)}%). Click to adjust cap.`
        : `AI spend this month · ${fmtUsd(spent)} (no cap set). Click to set a cap.`}
      data-testid="running-cost-pill"
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-full border px-2 font-mono-stat text-[11px] transition-colors',
        toneClass,
        'hover:brightness-105',
      )}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3" />}
      {fmtUsd(spent)}{cap != null ? <span className="opacity-70">/{fmtUsd(cap)}</span> : null}
    </button>
  );
}
